import React, { useState, useEffect } from 'react';
import FormatCard from './FormatCard';
import { renderMarkdown } from '../utils/markdown';

interface EngineViewProps {
  brand: string;
  piece: any;
  detail: any;
  onAction: (action: string, slug: string) => void;
  onToggleEngine: () => void;
  apiBase?: string;
}

function humanTitle(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function timeAgo(iso: string | undefined): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const TRUNK_STAGES = [
  { key: 'ingest', label: 'Source' },
  { key: 'research', label: 'Research' },
  { key: 'plan', label: 'Plan' },
  { key: 'compose', label: 'Narrative' },
];

export default function EngineView({ brand, piece, detail, onAction, apiBase }: EngineViewProps) {
  const fileBase = apiBase ? `${apiBase}/file` : `/api/${brand}/pieces/${piece.slug}/file`;
  const [narrativeMd, setNarrativeMd] = useState<string | null>(null);
  const [narrativeExpanded, setNarrativeExpanded] = useState(false);

  const stages = piece.stages || {};
  const renditions = piece.renditions || {};
  const publications = piece.publications || {};
  const configuredFormats: string[] = detail?.configuredFormats || Object.keys(renditions);
  const routing: Record<string, string[]> = detail?.routing || {};
  const validation = detail?.validation;
  const narrativeHash = detail?.narrativeHash || null;
  const allFormats = [...new Set([...configuredFormats, ...Object.keys(renditions)])];
  const allViolations = validation?.violations || [];

  // Staleness: compose output changed but render hasn't re-run
  const composeOutputHash = stages.compose?.outputHash;
  const renderInputHash = stages.render?.inputHash;
  const isStale = !!(composeOutputHash && renderInputHash && composeOutputHash !== renderInputHash);

  // #13 fix: Show violations for all formats that have renditions
  // Per-format violations come from validation.yaml if available, otherwise show global violations
  function violationsForFormat(fmt: string) {
    if (!renditions[fmt]) return [];
    // Check for per-format violations in validation data
    const perFormat = validation?.perFormat?.[fmt]?.violations;
    if (perFormat && Array.isArray(perFormat)) return perFormat;
    // For long-form formats, show all violations
    if (['blog', 'landing', 'newsletter', 'ebook-html'].includes(fmt)) return allViolations;
    // For social/reddit: filter to violations relevant to short-form (word count, readability are not relevant)
    const shortFormExclude = new Set(['word_count', 'reading_time', 'section_count', 'heading_count']);
    return allViolations.filter((v: any) => !shortFormExclude.has(v.metric));
  }

  // Load narrative markdown
  useEffect(() => {
    setNarrativeMd(null);
    setNarrativeExpanded(false);
    fetch(`${fileBase}/narrative.md`)
      .then(r => r.text())
      .then(setNarrativeMd)
      .catch(() => {});
  }, [piece.slug, brand]);

  return (
    <div className="engine-view">
      {/* Lineage Chain */}
      <div className="engine-view__lineage">
        <div className="engine-view__lineage-title">Lineage</div>
        <div className="engine-view__lineage-chain">
          {TRUNK_STAGES.map((ts, i) => {
            const rec = stages[ts.key];
            const hash = rec?.outputHash;
            return (
              <React.Fragment key={ts.key}>
                <div className={`engine-view__lineage-node ${hash ? 'engine-view__lineage-node--done' : ''}`}>
                  <div className="engine-view__lineage-label">{ts.label}</div>
                  {hash && <div className="engine-view__lineage-hash mono">{hash.slice(0, 8)}...</div>}
                  {rec?.completedAt && <div className="engine-view__lineage-time">{timeAgo(rec.completedAt)}</div>}
                </div>
                {i < TRUNK_STAGES.length - 1 && (
                  <div className={`engine-view__lineage-arrow ${hash ? 'engine-view__lineage-arrow--done' : ''}`}>{'\u2192'}</div>
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div className="engine-view__lineage-actions">
          {isStale ? (
            <button className="btn btn-primary btn--sm" onClick={() => onAction('pipeline', piece.slug)}>
              {'\u26A0'} Source changed — rewrite content
            </button>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--success)' }}>{'\u2713'} All outputs current</span>
          )}
        </div>
      </div>

      {/* Narrative (the trunk output — visible by default) */}
      {narrativeMd && (
        <div className="engine-view__narrative">
          <div className="engine-view__narrative-title">
            Narrative
            <span className="engine-view__narrative-hash mono">{narrativeHash?.slice(0, 12)}...</span>
          </div>
          <div
            className="engine-view__narrative-body"
            style={!narrativeExpanded ? { maxHeight: 280, overflow: 'hidden', maskImage: 'linear-gradient(to bottom, black 60%, transparent)', WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent)' } : undefined}
          >
            <div className="article-body" style={{ fontSize: 15 }}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(narrativeMd, { brand, slug: piece?.slug }) }}
            />
          </div>
          {!narrativeExpanded && (
            <button className="article-expand" onClick={() => setNarrativeExpanded(true)}>Read full narrative...</button>
          )}
        </div>
      )}

      {/* Fan-Out Grid */}
      <div className="engine-view__fanout">
        <div className="engine-view__fanout-title">Fan-Out ({allFormats.length} formats)</div>
        <div className="engine-view__fanout-grid">
          {allFormats.map(fmt => {
            const rend = renditions[fmt] || null;
            const dests = (routing[fmt] || []).map(ch => ({
              channel: ch,
              published: publications[ch] ? { permalink: (publications[ch] as any).permalink, publishedAt: (publications[ch] as any).publishedAt } : undefined,
            }));
            // If no routing but the format has a rendition, add all publication channels
            if (dests.length === 0 && rend) {
              for (const [ch, pub] of Object.entries(publications)) {
                dests.push({ channel: ch, published: { permalink: (pub as any).permalink, publishedAt: (pub as any).publishedAt } });
              }
            }

            return (
              <FormatCard
                key={fmt}
                brand={brand}
                slug={piece.slug}
                format={fmt}
                rendition={rend}
                narrativeHash={narrativeHash}
                destinations={dests}
                violations={violationsForFormat(fmt)}
                onAction={onAction}
                brandName={detail?.company?.name || piece.brand}
                isStale={isStale}
                fileBase={fileBase}
              />
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="engine-view__actions">
        <button className="btn btn-primary" onClick={() => onAction('pipeline', piece.slug)}>Rewrite content</button>
        <button className="btn btn-secondary" onClick={() => onAction('heal', piece.slug)}>Improve quality</button>
      </div>
    </div>
  );
}
